/**
 * contextRegistry.ts — Catálogo único de dados (variáveis) do Studio
 *
 * Cada módulo registra seu namespace com schema TIPADO. O modelo nunca conhece
 * SQL: ele referencia `{{proposal.total | money}}` e o núcleo resolve.
 * Coleções (`proposal.items[]`) são de primeira classe desde o início.
 *
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 1)
 */

import type { StudioContextDefinition, StudioContextField } from './types';
import { applyFormatter } from './formatters';

const registry = new Map<string, StudioContextDefinition>();

/** Registra (ou substitui) um namespace de dados. */
export function registerContext(definition: StudioContextDefinition): void {
  registry.set(definition.namespace, definition);
}

/** Retorna a definição de um namespace. */
export function getContext(namespace: string): StudioContextDefinition | undefined {
  return registry.get(namespace);
}

/** Lista os namespaces registrados, opcionalmente filtrando por permitidos. */
export function listContexts(permitidos?: string[]): StudioContextDefinition[] {
  const itens = Array.from(registry.values());
  return permitidos ? itens.filter((c) => permitidos.includes(c.namespace)) : itens;
}

/** Achata o schema em caminhos navegáveis (ex.: `customer.address.city`). */
export function listVariablePaths(permitidos?: string[]): Array<{
  path: string;
  label: string;
  type: StudioContextField['type'];
  example?: string;
}> {
  const saida: Array<{ path: string; label: string; type: StudioContextField['type']; example?: string }> = [];

  const percorrer = (prefixo: string, campos: Record<string, StudioContextField>) => {
    Object.entries(campos).forEach(([chave, campo]) => {
      const caminho = `${prefixo}.${chave}`;
      saida.push({
        path: caminho,
        label: campo.label ?? chave,
        type: campo.type,
        example: campo.example,
      });
      if (campo.type === 'object' && campo.fields) percorrer(caminho, campo.fields);
      if (campo.type === 'collection' && campo.itemSchema) percorrer(`${caminho}[]`, campo.itemSchema);
    });
  };

  listContexts(permitidos).forEach((ctx) => percorrer(ctx.namespace, ctx.schema));
  return saida;
}

/** Lê um caminho pontuado dentro de um objeto de dados. */
export function readPath(dados: Record<string, unknown>, caminho: string): unknown {
  return caminho.split('.').reduce<unknown>((atual, parte) => {
    if (atual === null || atual === undefined) return undefined;
    return (atual as Record<string, unknown>)[parte];
  }, dados);
}

/** Expressão de variável: `{{ namespace.campo | filtro }}`. */
const REGEX_VARIAVEL = /\{\{\s*([a-zA-Z0-9_.]+)\s*(?:\|\s*([a-zA-Z]+)\s*)?\}\}/g;

/**
 * Substitui as variáveis de um texto pelos valores resolvidos.
 * Variável sem valor vira string vazia — nunca aparece `{{...}}` para o cliente.
 */
export function resolveVariables(texto: string, dados: Record<string, unknown>): string {
  if (!texto) return '';
  return texto.replace(REGEX_VARIAVEL, (_todo, caminho: string, filtro?: string) =>
    applyFormatter(readPath(dados, caminho), filtro),
  );
}

/** Lista as variáveis usadas em um texto (para detecção na importação de HTML). */
export function extractVariables(texto: string): string[] {
  const encontrados = new Set<string>();
  let match: RegExpExecArray | null;
  const regex = new RegExp(REGEX_VARIAVEL.source, 'g');
  while ((match = regex.exec(texto)) !== null) encontrados.add(match[1]);
  return Array.from(encontrados);
}
